/**
 * DAFNE Time Period Configuration
 *
 * Defines meal times, analysis periods, and post-meal windows.
 * These can be customized per user but these are the defaults.
 *
 * Last reviewed: February 2026
 */

// =============================================================================
// MEAL/ENTRY PERIOD DEFINITIONS
// =============================================================================

export const PERIOD_HOURS = {
  /** Breakfast window (hours in 24h format) */
  B: { start: 5, end: 10 },

  /** Lunch window */
  L: { start: 10, end: 15 },

  /** Evening meal window */
  EM: { start: 15, end: 21 },

  /** Bedtime window */
  BT: { start: 21, end: 24 },

  /** 3AM check window (overnight) */
  "3AM": { start: 0, end: 5 },
} as const;

// =============================================================================
// DEFAULT MEAL TIMES (HH:MM format)
// =============================================================================

export const DEFAULT_MEAL_TIMES = {
  B: "07:30",
  L: "12:30",
  EM: "18:30",
  BT: "22:00",
  "3AM": "03:00",
} as const;

// =============================================================================
// PATTERN ANALYSIS TIME PERIODS
// =============================================================================

export const ANALYSIS_TIME_PERIODS = {
  dawn: { start: 5, end: 8, label: "Dawn (5-8 AM)" },
  morning: { start: 8, end: 12, label: "Morning (8 AM-12 PM)" },
  afternoon: { start: 12, end: 17, label: "Afternoon (12-5 PM)" },
  evening: { start: 17, end: 21, label: "Evening (5-9 PM)" },
  night: { start: 21, end: 24, label: "Night (9 PM-12 AM)" },
  overnight: { start: 0, end: 5, label: "Overnight (12-5 AM)" },
} as const;

// =============================================================================
// POST-MEAL ANALYSIS WINDOWS
// =============================================================================

export const POST_MEAL_WINDOWS = {
  /** Hours after meal to look for BG peak */
  B: { peakStart: 1, peakEnd: 3, label: "Post-Breakfast" },
  L: { peakStart: 1, peakEnd: 3, label: "Post-Lunch" },
  EM: { peakStart: 1, peakEnd: 3, label: "Post-Evening Meal" },
} as const;

// =============================================================================
// PERIOD SUGGESTION FUNCTION
// =============================================================================

/**
 * Suggest a period tag based on hour of day.
 *
 * @param hour - Hour in 24-hour format (0-23)
 * @returns Suggested period tag
 */
export function suggestPeriodFromHour(
  hour: number,
): "B" | "L" | "EM" | "BT" | "3AM" {
  if (hour >= PERIOD_HOURS.B.start && hour < PERIOD_HOURS.B.end) return "B";
  if (hour >= PERIOD_HOURS.L.start && hour < PERIOD_HOURS.L.end) return "L";
  if (hour >= PERIOD_HOURS.EM.start && hour < PERIOD_HOURS.EM.end) return "EM";
  if (hour >= PERIOD_HOURS.BT.start && hour < PERIOD_HOURS.BT.end) return "BT";
  return "3AM";
}
